import React, { useEffect } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Button, 
  Row, 
  Col, 
  Divider, 
  Typography,
  Space
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { COLORS } from '../../constants/colors';
import { StyledSelect, GlobalSelectStyles } from '../shared/StyledSelect';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ScreeningDataForm = ({ patient, onSave, onCancel, loading = false, initialData = {} }) => {
  const [form] = Form.useForm();

  const calculateBMI = (weight, height) => {
    if (!weight || !height) return null;
    const heightInMeters = height / 100;
    const bmi = weight / (heightInMeters * heightInMeters);
    return Math.round(bmi * 10) / 10;
  };

  // Watch form values to calculate BMI automatically
  const formValues = Form.useWatch([], form);
  
  useEffect(() => {
    if (formValues) {
      const { weight, height } = formValues;
      const bmi = calculateBMI(weight, height);
      if (bmi !== null && form.getFieldValue('bmi') !== bmi) {
        form.setFieldsValue({ bmi });
      }
    }
  }, [formValues, form]);

  const handleSubmit = async (values) => {
    const screeningData = {
      patientId: patient?._id || patient?.patientId,
      ...values,
      timestamp: new Date().toISOString(),
      technician: 'Current User' // This should come from auth context
    };
    
    await onSave(screeningData);
  };
  return (
    <>
      <GlobalSelectStyles />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialData}
        autoComplete="off"
      >
      <Title level={5} style={{ color: COLORS.textDark, marginBottom: 16 }}>
        Vital Signs
      </Title>
      
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Systolic BP (mmHg)"
            name="systolicBP"
            rules={[{ required: true, message: 'Please enter systolic BP' }]}
          >
            <InputNumber 
              placeholder="120" 
              min={50} 
              max={300} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Diastolic BP (mmHg)"
            name="diastolicBP"
            rules={[{ required: true, message: 'Please enter diastolic BP' }]}
          >
            <InputNumber 
              placeholder="80" 
              min={30} 
              max={200} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Heart Rate (bpm)"
            name="heartRate"
            rules={[{ required: true, message: 'Please enter heart rate' }]}
          >
            <InputNumber 
              placeholder="72" 
              min={30} 
              max={200} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Height (cm)"
            name="height"
          >
            <InputNumber 
              placeholder="170" 
              min={100} 
              max={250} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Weight (kg)"
            name="weight"
          >
            <InputNumber 
              placeholder="70" 
              min={20} 
              max={300} 
              step={0.1}
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="BMI"
            name="bmi"
          >
            <InputNumber 
              placeholder="Calculated automatically" 
              disabled
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Temperature (°C)"
            name="temperature"
          >
            <InputNumber 
              placeholder="36.5" 
              min={30} 
              max={45} 
              step={0.1}
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={5} style={{ color: COLORS.textDark, marginBottom: 16 }}>
        Blood Tests
      </Title>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Total Cholesterol (mg/dL)"
            name="totalCholesterol"
          >
            <InputNumber 
              placeholder="200" 
              min={50} 
              max={500} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="HDL Cholesterol (mg/dL)"
            name="hdlCholesterol"
          >
            <InputNumber 
              placeholder="50" 
              min={10} 
              max={200} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="LDL Cholesterol (mg/dL)"
            name="ldlCholesterol"
          >
            <InputNumber 
              placeholder="100" 
              min={10} 
              max={400} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Triglycerides (mg/dL)"
            name="triglycerides"
          >
            <InputNumber 
              placeholder="150" 
              min={10} 
              max={1000} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Blood Glucose (mg/dL)"
            name="bloodGlucose"
          >
            <InputNumber 
              placeholder="90" 
              min={30} 
              max={500} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Fasting Status"
            name="fastingStatus"
          >            <StyledSelect placeholder="Select fasting status">
              <Option value="fasting">Fasting (8+ hours)</Option>
              <Option value="non_fasting">Non-fasting</Option>
              <Option value="unknown">Unknown</Option>
            </StyledSelect>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            label="PSA (ng/mL)"
            name="psa"
          >
            <InputNumber 
              placeholder="4" 
              min={0} 
              max={100} 
              step={0.1}
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="A1C (%)"
            name="a1c"
          >
            <InputNumber 
              placeholder="5.7" 
              min={3} 
              max={20} 
              step={0.1}
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Lipoprotein(a) (nmol/L)"
            name="lipoproteinA"
          >
            <InputNumber 
              placeholder="75" 
              min={0} 
              max={500} 
              style={{ width: '100%', height: '48px' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={5} style={{ color: COLORS.textDark, marginBottom: 16 }}>
        Additional Assessments
      </Title>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Risk Assessment"
            name="riskAssessment"
          >            <StyledSelect placeholder="Select risk level">
              <Option value="low">Low Risk</Option>
              <Option value="moderate">Moderate Risk</Option>
              <Option value="high">High Risk</Option>
              <Option value="very_high">Very High Risk</Option>
            </StyledSelect>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Follow-up Required"
            name="followUpRequired"
          >            <StyledSelect placeholder="Select follow-up needs">
              <Option value="none">No Follow-up Needed</Option>
              <Option value="routine">Routine Follow-up</Option>
              <Option value="urgent">Urgent Follow-up</Option>
              <Option value="specialist">Specialist Referral</Option>
            </StyledSelect>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Clinical Notes"
        name="clinicalNotes"
      >
        <TextArea 
          placeholder="Enter any additional observations or notes..."
          rows={4}
          style={{ minHeight: '48px' }}
        />
      </Form.Item>

      <Form.Item
        label="Recommendations"
        name="recommendations"
      >
        <TextArea 
          placeholder="Enter recommendations for the patient..."
          rows={3}
          style={{ minHeight: '48px' }}
        />
      </Form.Item>

      <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel} style={{ height: '48px' }}>
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            icon={<SaveOutlined />}
            style={{ 
              background: COLORS.$primaryBlue, 
              borderColor: COLORS.$primaryBlue,
              boxShadow: 'none',
              height: '48px'
            }}
          >
            Save Screening Data
          </Button>
        </Space>
      </Form.Item>    </Form>
    </>
  );
};

export default ScreeningDataForm;
